import React, {useCallback} from 'react';

import {Field} from '../field';
import {Button, ButtonAppearance} from '../button';
import {removeArrayElementByIndex, useUniqueId} from '../../../utils';
import {CommonInputInterface} from '../common-input-interface';

import css from './button-group.module.css';

type ComboboxInterfaceCommon<T, U> = {
	items?: T[];
	value?: U | null;
	name?: string;
	onChange(value: U): void;
	/* значение для li */
	itemToHtml?(item: T | null): string | React.ReactNode;
	/* Ключ для T */
	itemToPrimaryKey?(value: T): string | number;
	/* подсказка в пустом поле */
	placeholder?: string;
	appearance?: ButtonAppearance;
} & Omit<CommonInputInterface<U | null>, 'buttons' | 'noButtons' | 'appearance' | 'disabledClear'>;

export type ComboboxInterface<T> =
	| ({multiple: true} & ComboboxInterfaceCommon<T, T[]>)
	| ({multiple?: false} & ComboboxInterfaceCommon<T, T>);

type Props<T> = ComboboxInterface<T> & Omit<React.HTMLAttributes<HTMLDivElement>, keyof ComboboxInterface<T>>;

export function ButtonGroup<T>(props: Props<T>) {
	const {
		label,
		itemToHtml: itemToHtmlProps,
		disabled,
		placeholder,
		items,
		itemToPrimaryKey: itemToPrimaryKeyProps,
		/* Убираем из divProps (это атрибуты с U) */
		multiple,
		value,
		defaultValue,
		onChange,
		/* Убираем из divProps (это атрибуты commonProps) */
		error,
		name,
		size,
		appearance,
		shape,
		...divProps
	} = props;

	const itemToHtml = itemToHtmlProps || ((item) => (typeof item === 'string' ? item : JSON.stringify(item)));
	const defaultItemToPrimaryKey = useCallback((value: T) => {
		if (!value) return null;

		if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
			return value;
		}
		if (!(typeof value === 'object' && 'id' in value) && !(typeof value === 'object' && 'code' in value)) {
			throw new Error('У значения нет id. Необходимо указать метод itemToPrimaryKey!');
		}
		return (value as any).id || (value as any).code;
	}, []);
	const itemToPrimaryKey = itemToPrimaryKeyProps || defaultItemToPrimaryKey;

	const handleChange = useCallback(
		(newValue: T) => {
			if (multiple) {
				const index =
					value && value.findIndex((value) => itemToPrimaryKey(value) === itemToPrimaryKey(newValue));
				if (typeof index === 'number' && index !== -1 && value) {
					onChange(removeArrayElementByIndex(value, index));
				} else {
					onChange([...(value || []), newValue]);
				}
			} else {
				onChange(newValue);
				close();
			}
		},
		[value, multiple, onChange, itemToPrimaryKey],
	);

	const generatedId = useUniqueId('Input');
	const id = props.id || generatedId;

	return (
		<Field commonProps={props} inputId={id} {...divProps}>
			<div className={css.wrapper}>
				{items?.map((item) => {
					const selected = (multiple ? (value as T[]) : [value]) || [];
					return (
						<Button
							key={itemToPrimaryKey(item)}
							checked={selected.includes(item)}
							onClick={() => handleChange(item)}
							size={size}
							shape={shape}
							appearance={appearance}
						>
							{itemToHtml(item)}
						</Button>
					);
				})}
			</div>
		</Field>
	);
}
